import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Drawer,
  EmptyState,
  Field,
  IconBtn,
  Input,
  Select,
  useConfirm,
  useToast,
} from "../../../../design-system";
import {
  createInternalDocumentType,
  deleteInternalDocumentType,
  updateInternalDocumentType,
} from "../../../../helpers/internal_documentation_helper";
import { TEXTAREA_STYLE } from "../../constants";
import { levelLabel } from "../../helpers";

export function TypesCard({ types, levels, onChanged }) {
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ code: "", name: "", description: "", default_level_id: "" });

  const openCreate = () => {
    setEditing({});
    setForm({ code: "", name: "", description: "", default_level_id: "" });
    setError(null);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      code: t.code,
      name: t.name,
      description: t.description || "",
      default_level_id: t.default_level_id || "",
    });
    setError(null);
  };

  const close = () => {
    if (submitting) return;
    setEditing(null);
    setError(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.code.trim()) return setError("El código es obligatorio.");
    if (!form.name.trim()) return setError("El nombre es obligatorio.");

    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || null,
      default_level_id: form.default_level_id || null,
    };

    setSubmitting(true);
    try {
      if (editing?.id) {
        await updateInternalDocumentType(editing.id, payload);
        toast.success("Tipo actualizado");
      } else {
        await createInternalDocumentType(payload);
        toast.success("Tipo creado");
      }
      setEditing(null);
      onChanged?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudo guardar el tipo.");
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (t) => {
    const ok = await confirm({
      title: "Eliminar tipo",
      description: `Eliminar el tipo "${t.name}". Solo es posible si no hay documentos que lo usen.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await deleteInternalDocumentType(t.id);
      toast.success("Tipo eliminado");
      onChanged?.();
    } catch (e) {
      toast.error("No se pudo eliminar", typeof e === "string" ? e : e?.message);
    }
  };

  const levelLookup = (id) => (levels || []).find((l) => l.id === id);
  const formId = "doc-type-form";

  return (
    <Card padded={false}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "16px 20px",
          borderBottom: "1px solid var(--kr-border)",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--kr-fg-strong)" }}>
            Tipos documentales
          </div>
          <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 2 }}>
            Clasificación funcional dentro de la jerarquía (ej. Procedimiento, Instrucción técnica, Formato).
          </div>
        </div>
        <Button variant="primary" icon="Plus" onClick={openCreate}>
          Nuevo tipo
        </Button>
      </div>
      {(types || []).length === 0 ? (
        <EmptyState
          icon="Tag"
          title="Sin tipos"
          description="Define los tipos documentales que usará el workspace."
        />
      ) : (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "100px 1fr 1fr 1fr 100px",
              gap: 12,
              padding: "10px 20px",
              background: "var(--kr-neutral-50)",
              borderBottom: "1px solid var(--kr-border)",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              fontWeight: 600,
              color: "var(--kr-fg-muted)",
            }}
          >
            <span>Código</span>
            <span>Nombre</span>
            <span>Nivel por defecto</span>
            <span>Descripción</span>
            <span style={{ textAlign: "right" }}>Acciones</span>
          </div>
          {types.map((t, i) => {
            const lvl = levelLookup(t.default_level_id);
            return (
              <div
                key={t.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 1fr 1fr 1fr 100px",
                  gap: 12,
                  alignItems: "center",
                  padding: "10px 20px",
                  borderBottom: i === types.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
                  fontSize: 13,
                }}
              >
                <span style={{ fontFamily: "var(--kr-font-mono, ui-monospace, monospace)", fontSize: 12 }}>
                  {t.code}
                </span>
                <span style={{ color: "var(--kr-fg-strong)", fontWeight: 500 }}>{t.name}</span>
                <span style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>
                  {lvl ? levelLabel(lvl) : "—"}
                </span>
                <span style={{ color: "var(--kr-fg-muted)", fontSize: 12 }}>{t.description || "—"}</span>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
                  <IconBtn icon="PenLine" title="Editar" onClick={() => openEdit(t)} />
                  <IconBtn icon="Trash2" title="Eliminar" onClick={() => onDelete(t)} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Drawer
        open={editing !== null}
        onClose={close}
        size="sm"
        title={editing?.id ? "Editar tipo" : "Nuevo tipo"}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
              Cancelar
            </Button>
            <div style={{ flex: 1 }} />
            <Button variant="primary" type="submit" form={formId} disabled={submitting}>
              {submitting ? "Guardando…" : "Guardar"}
            </Button>
          </>
        }
      >
        <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {error && <Alert tone="danger">{error}</Alert>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
            <Field label="Código" required>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="Ej. PRC"
                required
              />
            </Field>
            <Field label="Nombre" required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Procedimiento"
                required
              />
            </Field>
          </div>
          <Field label="Nivel por defecto" help="Sugerencia al subir un documento de este tipo.">
            <Select
              value={form.default_level_id}
              onChange={(e) => setForm({ ...form, default_level_id: e.target.value })}
            >
              <option value="">— Ninguno —</option>
              {(levels || []).map((l) => (
                <option key={l.id} value={l.id}>
                  {levelLabel(l)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Descripción">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              style={TEXTAREA_STYLE}
            />
          </Field>
        </form>
      </Drawer>
    </Card>
  );
}
